import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, LinearProgress, Link, Typography } from '@mui/material';
import { IOrderDashboardRow } from '../../../interfaces/garments/merchReport.interface';
import { isLate, journeyPath, progressText } from './threadData';

interface Props {
    orderId: string;
    orderNumber: string;
    /** The order's row from the order dashboard endpoint; undefined when the viewer cannot read it or the order is not live. */
    row?: IOrderDashboardRow;
}

/**
 * The Progress cell of the buyer order list: a mini bar plus text such as "5% · 6 late" (red when late).
 * It opens the order thread page and never opens the order form. Without a row it is a plain labelled link.
 */
export default function OrderProgressCell({ orderId, orderNumber, row }: Props) {
    const navigate = useNavigate();
    const open = (e: React.SyntheticEvent) => { e.stopPropagation(); navigate(journeyPath(orderId)); };

    if (!row) {
        return (
            <Link
                component="button"
                type="button"
                variant="body2"
                data-help="merchandising.orders.journey"
                aria-label={`Thread of order ${orderNumber}`}
                onClick={open}
            >
                Thread
            </Link>
        );
    }

    const late = isLate(row);
    return (
        <Box
            component="button"
            type="button"
            data-help="merchandising.orders.journey"
            aria-label={`Thread of order ${orderNumber}: ${progressText(row)}`}
            onClick={open}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, p: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer', font: 'inherit', color: 'inherit', textAlign: 'left' }}
        >
            <LinearProgress
                variant="determinate"
                value={Math.max(0, Math.min(100, row.progressPct))}
                color={late ? 'error' : 'primary'}
                aria-hidden
                sx={{ flex: '1 1 48px', minWidth: 48, maxWidth: 96, height: 6, borderRadius: 3 }}
            />
            <Typography variant="caption" color={late ? 'error' : 'text.secondary'} sx={{ whiteSpace: 'nowrap', fontWeight: late ? 700 : 400 }}>
                {progressText(row)}
            </Typography>
        </Box>
    );
}
